/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IlCard, IlIconInitializer, IlLoading } from 'il-framework-component';
import { I18nNamespaces } from '../../../../app/shared/utils/constants.util';
import { useRequiredLevel } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';
import {
  setEnrichmentContext,
  setEnrichmentError,
  setEnrichmentStatus,
  EnrichmentStatus
} from '../../setupSecuritySlice';
import Wrapper from '../../../../app/shared/components/Wrapper/Wrapper';
import styles from './NonSSOLoaCallback.scss';
import { showToast } from '../../../../app/shared/AppSlice/AppSlice';
import { SecuritySetupRoutes } from '../../../../app/shared/utils/securitySetupRoutes';

const NonSsoLoaCallback: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const { replacePreserve } = usePreserveSearchNav();
  const { search } = useLocation();
  const requiredLevel = useRequiredLevel();

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const query = new URLSearchParams(search || '');
    const loaProof = query.get('loaProof') as any;

    if (!loaProof) {
      dispatch(setEnrichmentStatus(EnrichmentStatus.Error));
      dispatch(setEnrichmentError('loaEnrichment.sessionResult.missingLoaProof'));

      dispatch(showToast({
        title: t('loaEnrichment.common.errors.otpExpiredToast'),
        timeout: 0,
        kind: 'error',
        subtitle: ''
      }));

      replacePreserve(SecuritySetupRoutes.enrichment);
      return;
    }

    dispatch(setEnrichmentStatus(EnrichmentStatus.Loading));
    dispatch(setEnrichmentError(undefined));
    dispatch(setEnrichmentContext({
      loaProof,
      targetLevel: requiredLevel ?? 2
    }));

    replacePreserve(SecuritySetupRoutes.enrichment);
  }, [dispatch, search, replacePreserve, requiredLevel, t]);

  return (
    <>
      <IlIconInitializer />
      <Wrapper>
        <IlCard className={styles.cardForm}>
          <IlLoading withOverlay />
        </IlCard>
      </Wrapper>
    </>
  );
};

export default NonSsoLoaCallback;
